import { useMemo } from "react";
import { useAlarmsByCameras } from "@src/services/api/hooks";
import { prunePastExceptions } from "@src/features/alarms/utils/alarm-utils";
import { toLocalDate, makeGroupKey } from "@src/features/alarms/utils/exception-format";
import type { AggregatedExceptionGroup } from "@src/features/alarms/components/ManageExceptionsContent";

interface UseAggregatedExceptionsResult {
  isLoading: boolean;
  exceptions: AggregatedExceptionGroup[];
}

export function useAggregatedExceptions(siteId: string, cameraKeys: string[]): UseAggregatedExceptionsResult {
  const alarmQueries = useAlarmsByCameras(siteId, cameraKeys);
  const isLoading = alarmQueries.some((q) => q.isLoading);

  const exceptions = useMemo(() => {
    const now = new Date();
    const totalCameras = cameraKeys.length;
    const groups = new Map<string, AggregatedExceptionGroup>();

    for (let i = 0; i < alarmQueries.length; i++) {
      const config = alarmQueries[i].data;
      if (!config?.settings) continue;
      const tz = config.settings.timezone ?? "UTC";
      const upcoming = prunePastExceptions(
        config.settings.exceptions.filter((e) => e.period?.start && e.period?.end),
        now,
        tz,
      );
      const cameraKey = cameraKeys[i];
      for (const ex of upcoming) {
        const key = makeGroupKey(ex);
        const existing = groups.get(key);
        if (existing) {
          existing.cameraKeys.push(cameraKey);
        } else {
          groups.set(key, {
            key,
            reason: ex.reason,
            action: ex.action,
            period: ex.period,
            cameraKeys: [cameraKey],
            totalCameras,
            timezone: tz,
          });
        }
      }
    }

    return [...groups.values()].sort((a, b) => {
      const aDate = toLocalDate(a.period.start, a.timezone);
      const bDate = toLocalDate(b.period.start, b.timezone);
      return aDate.getTime() - bDate.getTime();
    });
  }, [alarmQueries, cameraKeys]);

  return { isLoading, exceptions };
}
